import React, { useState } from "react";
import {
  PrivateOfficeUpdateRequest,
  PictureProperty,
  CurrentRoomImages,
} from "@/services/private-office-service/types";
import { ImageSelectionGallery } from "@/components/ui/image-selection-gallery";
import {
  getNewImageUrl,
  convertToRoomImageArray,
  isImageRequest,
  isImageDeletionRequest,
  extractImagesFromOfficeInfo,
  getRequestImageProperty,
} from "@/utils/image-utils";
import { Button } from "@/components/ui/button";
import { privateOfficeService } from "@/services/private-office-service";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/features/authentication";
import { extractApiErrorMessage } from "@/utils/extract-api-error-message";
import { AiImageComparisonDialog } from "./ai-image-comparison-dialog";

interface PendingImageRequestProps {
  request: PrivateOfficeUpdateRequest;
  onApproved: (req: PrivateOfficeUpdateRequest) => void;
  onRejected: (req: PrivateOfficeUpdateRequest) => void;
  /**
   * Room slots as they stand now, i.e. the request snapshot merged with the
   * slots already written by approvals made in this session. `office_info` is
   * frozen when the request is created, so on a room with more than one pending
   * image request it keeps showing a slot as free after a sibling filled it —
   * and approving the sibling into that same slot loses the first photo.
   */
  roomImages?: CurrentRoomImages;
  onImageSlotApplied?: (
    roomId: number,
    property: PictureProperty,
    url: string | null,
  ) => void;
}

export const PendingImageRequest: React.FC<PendingImageRequestProps> = ({
  request,
  onApproved,
  onRejected,
  roomImages,
  onImageSlotApplied,
}) => {
  const [selectedProperty, setSelectedProperty] =
    useState<PictureProperty | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [optimizedImageUrl, setOptimizedImageUrl] = useState<string | null>(
    null,
  );
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const { userInfo } = useAuth();

  if (
    !isImageRequest(request.fields_to_update) ||
    isImageDeletionRequest(request.fields_to_update)
  ) {
    return null;
  }

  const newImageUrl = getNewImageUrl(request.fields_to_update);
  const currentImages = convertToRoomImageArray(
    roomImages ?? extractImagesFromOfficeInfo(request.office_info),
  );

  // Check if this is an addition (no existing images) or a replacement
  const hasExistingImages = currentImages.some((img) => !img.isEmpty);
  const isImageAddition = !hasExistingImages;

  const handleApprove = async (overrideUrl?: string) => {
    try {
      setIsApproving(true);

      const approvalData: {
        userEmail: string;
        new_image_url?: string;
        replace_property?: PictureProperty;
      } = {
        userEmail: userInfo.profile?.email || "",
      };

      const derivedProperty =
        selectedProperty ?? getRequestImageProperty(request.fields_to_update);

      if (overrideUrl) {
        // Aprovação via modal de melhoramento — usa URL otimizada
        approvalData.new_image_url = overrideUrl;
        approvalData.replace_property = derivedProperty ?? undefined;
      } else if (!isImageAddition && selectedProperty) {
        approvalData.replace_property = selectedProperty;
      }

      const data = await privateOfficeService.approveUpdateRequest(
        request._id,
        approvalData,
      );

      toast({
        title: "Sucesso",
        description: "Solicitação aprovada com sucesso",
      });
      setShowComparisonModal(false);
      setOptimizedImageUrl(null);

      const resolvedProperty = overrideUrl ? derivedProperty : selectedProperty;
      const approvedImageUrl = overrideUrl ?? newImageUrl;
      const updatedData = resolvedProperty
        ? {
            ...data,
            office_info: {
              ...data.office_info,
              [resolvedProperty]: approvedImageUrl,
            },
          }
        : data;

      // Marks the slot as taken for the sibling requests of the same room.
      // Falls back to the slot the request itself names, which is where an
      // addition (no `replace_property` sent) lands.
      const appliedProperty = resolvedProperty ?? derivedProperty;
      if (appliedProperty) {
        onImageSlotApplied?.(
          request.office_info.id,
          appliedProperty,
          approvedImageUrl,
        );
      }

      onApproved(updatedData);
    } catch (error) {
      console.error("Erro ao aprovar solicitação:", error);
      toast({
        title: "Erro",
        description: extractApiErrorMessage(
          error,
          "Erro ao aprovar solicitação",
        ),
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleOptimize = async () => {
    try {
      setIsOptimizing(true);
      const result = await privateOfficeService.optimizeImage({
        imageUrl: newImageUrl,
      });
      setOptimizedImageUrl(result.enhancedUrl);
      setShowComparisonModal(true);
    } catch (error) {
      console.error("Erro ao otimizar imagem:", error);
      toast({
        title: "Erro",
        description: "Não foi possível otimizar a imagem. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsRejecting(true);
      const data = await privateOfficeService.rejectUpdateRequest(
        request._id,
        rejectionReason,
      );

      toast({
        title: "Sucesso",
        description: "Solicitação reprovada",
      });
      onRejected(data);
    } catch (error) {
      console.error("Erro ao reprovar solicitação:", error);
      toast({
        title: "Erro",
        description: extractApiErrorMessage(
          error,
          "Erro ao reprovar solicitação",
        ),
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
      setShowRejectionForm(false);
      setRejectionReason("");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Aprovação de Imagem - {request.office_info.name}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Solicitado por {request.requested_by.username} em{" "}
          {new Date(request.created_at).toLocaleDateString("pt-BR")}
        </p>
      </div>

      <div className="space-y-8">
        <div className="text-center">
          <h4 className="font-medium text-gray-900 mb-4">
            Nova Imagem Proposta
          </h4>
          <div className="relative max-w-sm mx-auto">
            <div
              className="group aspect-video rounded-xl overflow-hidden shadow-lg ring-1 ring-gray-200 cursor-pointer hover:ring-2 hover:ring-[#F9E10D] transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                setShowImageModal(true);
              }}
            >
              <img
                src={newImageUrl}
                alt="Nova imagem proposta"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
              />
              {request.additional_data?.tags && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b">
                  {request.additional_data?.tags?.join(", ")}
                </div>
              )}
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#F9E10D] rounded-full flex items-center justify-center shadow-lg">
              <span className="text-black text-xs font-bold">+</span>
            </div>
          </div>
          <div className="mt-3 flex justify-center">
            <Button
              variant="outline"
              onClick={handleOptimize}
              disabled={isOptimizing || isApproving}
              loading={isOptimizing}
              className="w-full max-w-sm"
            >
              ✨ Otimizar com IA
            </Button>
          </div>
        </div>

        {!isImageAddition && (
          <div className="border-t border-gray-100 pt-6">
            <h4 className="font-medium text-gray-900 mb-4 text-center">
              Selecione qual imagem será substituída
            </h4>
            <ImageSelectionGallery
              images={currentImages}
              selectedProperty={selectedProperty}
              onSelect={setSelectedProperty}
            />
          </div>
        )}

        {isImageAddition && (
          <div className="border-t border-gray-100 pt-6">
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Nova Imagem</h4>
              <p className="text-sm text-gray-600">
                Esta imagem será adicionada à galeria da sala.
              </p>
            </div>
          </div>
        )}
      </div>

      {!showRejectionForm ? (
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleApprove();
            }}
            disabled={(!isImageAddition && !selectedProperty) || isApproving}
            loading={isApproving}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Aprovar
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setShowRejectionForm(true);
            }}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            Reprovar
          </Button>
        </div>
      ) : (
        <div className="pt-4 border-t border-gray-200 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo da reprovação:
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Digite o motivo da reprovação..."
            />
          </div>
          <div className="flex gap-3">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleReject();
              }}
              loading={isRejecting}
              disabled={isRejecting || !rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Confirmar Reprovação
            </Button>
            <Button
              disabled={isRejecting}
              onClick={(e) => {
                e.stopPropagation();
                setShowRejectionForm(false);
                setRejectionReason("");
              }}
              variant="outline"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Modal para visualizar imagem ampliada */}
      {showImageModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={newImageUrl}
              alt="Nova imagem proposta - Visualização ampliada"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <AiImageComparisonDialog
        isOpen={showComparisonModal}
        originalUrl={newImageUrl}
        optimizedUrl={optimizedImageUrl ?? ""}
        isApproving={isApproving}
        onApprove={() => optimizedImageUrl && handleApprove(optimizedImageUrl)}
        onClose={() => {
          setShowComparisonModal(false);
          setOptimizedImageUrl(null);
        }}
      />
    </div>
  );
};
